/**
 * Timezone utility functions using Google Maps Time Zone API
 * Provides accurate timezone detection based on coordinates
 */

// Simple cache for timezone lookups
const timezoneCache = new Map();
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Get timezone from browser (fallback method)
 * @returns {string|null} Browser timezone or null if not available
 */
const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (error) {
    console.warn("Browser timezone not available:", error);
    return null;
  }
};

/**
 * Get UTC offset in minutes for a given timezone
 * @param {string} tz - IANA timezone string
 * @returns {number} Offset in minutes from UTC
 */
const getTimezoneOffset = (tz) => {
  const date = new Date();
  const utc = date.toLocaleString("en-US", { timeZone: "UTC" });
  const local = date.toLocaleString("en-US", { timeZone: tz });
  return (new Date(utc) - new Date(local)) / 60000;
};

/**
 * Try to resolve an Etc/GMT+N timezone to a friendlier IANA name
 * by comparing offsets with the browser timezone
 * @param {string} etcTimezone - Timezone in Etc/GMT+N format
 * @returns {string} Resolved timezone (friendlier name if possible)
 */
const resolveEtcTimezone = (etcTimezone) => {
  try {
    const browserTz = getBrowserTimezone();
    if (browserTz && !browserTz.startsWith("Etc/")) {
      if (getTimezoneOffset(etcTimezone) === getTimezoneOffset(browserTz)) {
        return browserTz;
      }
    }
  } catch {
    // If offset comparison fails, keep the original
  }
  return etcTimezone;
};

/**
 * Get timezone from coordinates using Google Maps Time Zone API (most accurate)
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {string} apiKey - Google Maps API key
 * @returns {Promise<string|null>} Timezone string or null if error
 */
export const getTimezoneFromCoordinates = async (lat, lng, apiKey) => {
  if (lat == null || lng == null || !apiKey) {
    return null;
  }

  try {
    const timestamp = Math.floor(Date.now() / 1000);
    const url = `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${apiKey}`;

    const response = await fetch(url);
    const data = await response.json();

    if (data.status === "OK" && data.timeZoneId) {
      return data.timeZoneId;
    } else {
      return null;
    }
  } catch {
    return null;
  }
};

/**
 * Check if coordinates are in cache and still valid
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {string|null} Cached timezone or null
 */
const getCachedTimezone = (lat, lng) => {
  try {
    const key = `${Number(lat).toFixed(4)},${Number(lng).toFixed(4)}`;
    const cached = timezoneCache.get(key);

    if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) {
      return cached.timezone;
    }

    // Remove expired entry
    if (cached) {
      timezoneCache.delete(key);
    }
  } catch {
    // If lat/lng are not valid numbers, skip cache
  }

  return null;
};

/**
 * Cache timezone for coordinates
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {string} timezone - Timezone to cache
 */
const cacheTimezone = (lat, lng, timezone) => {
  try {
    const key = `${Number(lat).toFixed(4)},${Number(lng).toFixed(4)}`;
    timezoneCache.set(key, {
      timezone,
      timestamp: Date.now(),
    });
  } catch {
    // If lat/lng are not valid numbers, skip caching
  }
};

/**
 * Get timezone from location using Google Maps Time Zone API
 * Priority: cache -> Time Zone API -> browser (fallback)
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {string} apiKey - Google Maps API key (required)
 * @returns {Promise<string|null>} Timezone string or null if error
 */
export const getTimezoneFromLocation = async (lat, lng, apiKey) => {
  if (!apiKey) {
    console.warn("Google Maps API key is required for timezone detection");
    return getBrowserTimezone() || "UTC";
  }

  // 1. Check cache first (free)
  let timezone = getCachedTimezone(lat, lng);
  if (timezone) {
    return timezone;
  }

  // 2. Use Google Maps Time Zone API (most accurate)
  timezone = await getTimezoneFromCoordinates(lat, lng, apiKey);
  if (timezone) {
    // Resolve Etc/GMT+N to friendlier IANA name when possible
    if (timezone.startsWith("Etc/")) {
      timezone = resolveEtcTimezone(timezone);
    }
    cacheTimezone(lat, lng, timezone);
    return timezone;
  }

  // 3. Try browser timezone as fallback
  timezone = getBrowserTimezone();
  if (timezone) {
    cacheTimezone(lat, lng, timezone);
    return timezone;
  }

  // 4. Fallback to UTC
  console.warn("No timezone found, using UTC as fallback");
  return "UTC";
};

/**
 * Validate timezone using the browser's Intl API
 * @param {string} timezone - Timezone string to validate
 * @returns {boolean} True if valid timezone
 */
export const isValidTimezone = (timezone) => {
  if (!timezone || typeof timezone !== "string") {
    return false;
  }

  if (timezone === "UTC") {
    return true;
  }

  try {
    new Intl.DateTimeFormat([], { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};
